import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { scrollToId } from "../lib/scroll";
import { MaskLines, Magnetic, EASE } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1640357897497-599b4fc84f51?q=80&w=2400&auto=format&fit=crop";

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden flex">
      <motion.div className="absolute inset-0" style={{ y }}>
        <motion.img
          src={HERO_IMG}
          alt="Interno di lusso progettato da Green Gate"
          className="w-full h-[120%] object-cover"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.6, ease: EASE }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/55 to-ink/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/70" />
      </motion.div>

      <div className="absolute inset-4 md:inset-6 z-[5] border border-cream/10 pointer-events-none" aria-hidden="true" />

      <motion.div
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full flex flex-col justify-end items-center text-center pb-24 md:pb-28 pt-44"
        style={{ opacity }}
      >
        <motion.div
          className="flex items-center gap-4 mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.9 }}
        >
          <span className="h-px w-12 bg-brass" />
          <p className="eyebrow">{t.hero.overline}</p>
          <span className="h-px w-12 bg-brass" />
        </motion.div>

        <h1 className="font-serif font-light text-cream text-[13.5vw] sm:text-7xl lg:text-8xl xl:text-[7.5rem] leading-[1.02] tracking-tight">
          <MaskLines lines={t.hero.lines} delay={2.0} lineClass="pb-[0.12em] -mb-[0.12em]" />
        </h1>

        <motion.div
          className="mt-10 flex flex-wrap items-center justify-center gap-6"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 2.8, ease: EASE }}
        >
          <Magnetic>
            <button
              data-testid="hero-cta-portfolio"
              onClick={() => scrollToId("#portfolio")}
              className="inline-flex items-center gap-3 text-[13px] uppercase tracking-[0.16em] text-cream border-b border-cream/40 pb-2 hover:text-brass hover:border-brass transition-colors duration-400"
            >
              {t.hero.ctaPrimary}
            </button>
          </Magnetic>
          <Magnetic>
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollToId("#contatti")}
              className="inline-flex items-center gap-3 text-[13px] uppercase tracking-[0.16em] text-cream border-b border-cream/40 pb-2 hover:text-brass hover:border-brass transition-colors duration-400"
            >
              {t.hero.ctaSecondary}
            </button>
          </Magnetic>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#chi-siamo")}
        className="absolute bottom-8 right-6 md:right-12 z-10 hidden md:flex flex-col items-center gap-3 text-stone hover:text-brass transition-colors duration-300"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.2, duration: 1 }}
        aria-label="Scorri verso il basso"
      >
        <span className="eyebrow text-[10px]">{t.hero.location}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2 }}>
          <ArrowDown size={18} strokeWidth={1.25} />
        </motion.span>
      </motion.button>
    </section>
  );
}
